import React, { useEffect, useRef, useState } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import {
  clampChartHeight,
  getAuthenticatedWebSocketUrl,
  getPreferredChartHeight,
  savePreferredChartHeight,
  setSessionVariable,
  sortForwardTenors,
} from "../Utils/CoTraderUtils";
import CoTraderForwardChart from "../Components/CoTraderForwardChartTileComponent";
import BestxSummaryTileComponent from "../Components/BestX/BestXSummaryTileComponent";
import BestXPriceChartTileComponent from "../Components/BestX/BestXPriceChartTile";
import { BestXTradeHeader } from "../Components/BestX/BestXTradeHeader";
import BestXPopOverMenu from "../Components/BestX/BestXPopOverMenu";
import BestXConversationTileComponent from "../Components/BestX/BestXConversationTileComponent";
import BestXLandingPage from "../Components/BestX/BestXLandingPage";
import { parseBestXTradeContext } from "../Utils/bestXTradeContext";
import { toDateInputValue } from "../Components/shared/dateTimeInput";
interface Context {
  id?: {
    [key: string]: string;
  };
  name?: string;
  type: string;
  [s: string]: any;
}
interface ContextProp {
  context: Context;
  currencyList: string[]; // Ensure that the type of the prop is defined
}

type BestXLandingField =
  | "currencyPair"
  | "executionTimestamp"
  | "executionSpotRate"
  | "executionForwardPoints"
  | "expirationTimestamp";

type BestXLandingPrefill = {
  currencyPair: string;
  executionTimestamp: string;
  executionSpotRate: number | null;
  executionForwardPoints: number | null;
  expirationTimestamp: string;
};

type BestXSubmittedTrade = BestXLandingPrefill & {
  useContext?: boolean;
};

type BestXAnalysisWindow = "1d" | "1w";
type BestXChartTab = "split" | "prices" | "forward";

type BestXAnalysisSources = {
  prices: boolean;
  trading_economics: boolean;
  realtime_news: boolean;
  truth_social: boolean;
};

const DEFAULT_ANALYSIS_WINDOW: BestXAnalysisWindow = "1d";
const DEFAULT_ANALYSIS_SOURCES: BestXAnalysisSources = {
  prices: true,
  trading_economics: true,
  realtime_news: true,
  truth_social: false,
};
const BESTX_REQUEST_TIME_ZONE = "UTC";
const BESTX_PRICE_WINDOW_MS = 60 * 60 * 1000;
const WIDE_ANALYSIS_LAYOUT_QUERY = "(min-width: 1100px)";
const SUMMARY_SOURCE_OPTIONS: {
  key: keyof BestXAnalysisSources;
  label: string;
}[] = [
  { key: "prices", label: "Price data" },
  { key: "trading_economics", label: "Economic Data Releases" },
  { key: "realtime_news", label: "Real-time News" },
  { key: "truth_social", label: "Social Media" },
];
const COMPACT_CHART_BREAKPOINT = 580;
const isCompactChartLayout = () =>
  typeof window !== "undefined" &&
  window.innerWidth <= COMPACT_CHART_BREAKPOINT;

const ANALYSIS_WINDOW_LABELS: Record<BestXAnalysisWindow, string> = {
  "1d": "± 1 day",
  "1w": "± 1 week",
};

const ANALYSIS_SOURCE_LABELS: Record<keyof BestXAnalysisSources, string> = {
  prices: "Price Data",
  trading_economics: "Economic Data Releases",
  realtime_news: "Real-time News",
  truth_social: "Social Media",
};

const getBestXMissingFields = (trade: BestXLandingPrefill) => {
  const missingFields: BestXLandingField[] = [];
  if (!trade.currencyPair) missingFields.push("currencyPair");
  if (!trade.executionTimestamp) missingFields.push("executionTimestamp");
  return missingFields;
};

const parseSummaryPayload = (message: any) => {
  if (message == null) return null;

  if (typeof message === "string") {
    try {
      return JSON.parse(message);
    } catch {
      return null;
    }
  }

  return message;
};

const formatSummaryUrlList = (rawUrls: string) => {
  const urls = rawUrls.split(/\s+/).filter(Boolean);
  if (urls.length === 0) return "";
  return `\n\n### Sources\n${urls.map((url) => `- ${url}`).join("\n")}`;
};

const BestXTile1: React.FunctionComponent<ContextProp> = ({
  context,
  currencyList,
}) => {
  const theme = useTheme();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<"summary" | "chat">("summary");
  const [isWideLayout, setIsWideLayout] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(WIDE_ANALYSIS_LAYOUT_QUERY).matches
  );
  const [activeChartTab, setActiveChartTab] = useState<BestXChartTab>(
    isCompactChartLayout() ? "prices" : "split"
  );
  const [chartHeight, setChartHeight] = useState(getPreferredChartHeight);
  const [useCompactCharts, setUseCompactCharts] = useState(isCompactChartLayout);
  const [isSummaryConfigOpen, setIsSummaryConfigOpen] = useState(false);
  const [autoGenerateSummary, setAutoGenerateSummary] = useState(false);
  const [currencyPair, setCurrencyPair] = useState("");
  const [useContext, setUseContext] = useState(true);
  const [executionTimestamp, setExecutionTimestamp] = useState("");
  const [executionSpotRate, setExecutionSpotRate] = useState<number | null>(null);
  const [executionForwardPoints, setExecutionForwardPoints] = useState<number | null>(null);
  const [expiration_timestamp, setExpiration_timestamp] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<string>("");
  const summaryCacheRef = useRef("");
  const summaryVersionRef = useRef(0);
  const summaryRequestCleanupRef = useRef<(() => void) | null>(null);
  const [analysisWindow, setAnalysisWindow] =
    useState<BestXAnalysisWindow>(DEFAULT_ANALYSIS_WINDOW);
  const [analysisSources, setAnalysisSources] = useState<BestXAnalysisSources>(
    DEFAULT_ANALYSIS_SOURCES
  );
  const [userLanguage, setUserLanguage] = useState(
    localStorage.getItem("user_language") || "English",
  );
  const [tenorList, setTenorList] = useState([]);
  const [tenors, setTenors]: any = useState([]);
  const [landingPrefill, setLandingPrefill] = useState<BestXLandingPrefill | null>(null);
  const [landingNotice, setLandingNotice] = useState("");
  const [landingRequiredFields, setLandingRequiredFields] = useState<
    BestXLandingField[]
  >([]);
  const [submissionVersion, setSubmissionVersion] = useState(0);
  const handleTradeSubmit = (trade: BestXSubmittedTrade) => {
    if (summaryRequestCleanupRef.current) {
      summaryRequestCleanupRef.current();
      summaryRequestCleanupRef.current = null;
    }
    setCurrencyPair(trade.currencyPair);
    setExecutionTimestamp(trade.executionTimestamp);
    setExecutionSpotRate(trade.executionSpotRate);
    setExecutionForwardPoints(trade.executionForwardPoints);
    setExpiration_timestamp(trade.expirationTimestamp);
    setSummary("");
    summaryCacheRef.current = "";
    summaryVersionRef.current = 0;
    setAnalysisWindow(DEFAULT_ANALYSIS_WINDOW);
    setAnalysisSources(DEFAULT_ANALYSIS_SOURCES);
    setLandingNotice("");
    setLandingRequiredFields([]);
    setLandingPrefill(null);
    setIsSummaryConfigOpen(false);
    setAutoGenerateSummary(true);
    setUseContext(trade.useContext ?? false);
    setHasSubmitted(true);
    setActiveTab("summary");
    setActiveChartTab("prices");
    setLoading(false);
    setTenorList([]);
    setTenors([]);
    setSubmissionVersion((version) => version + 1);
  };
  const handleSetTenorList = React.useCallback(
    (nextTenorList: string[] = []) => {
      setTenorList(sortForwardTenors(nextTenorList) as any);
    },
    []
  );
  const handleResetToLanding = () => {
    if (summaryRequestCleanupRef.current) {
      summaryRequestCleanupRef.current();
      summaryRequestCleanupRef.current = null;
    }
    summaryCacheRef.current = "";
    summaryVersionRef.current = 0;
    setHasSubmitted(false);
    setUseContext(true);
    setCurrencyPair("");
    setExecutionTimestamp("");
    setExecutionSpotRate(null);
    setExecutionForwardPoints(null);
    setExpiration_timestamp("");
    setSummary("");
    setAnalysisWindow(DEFAULT_ANALYSIS_WINDOW);
    setAnalysisSources(DEFAULT_ANALYSIS_SOURCES);
    setIsSummaryConfigOpen(false);
    setAutoGenerateSummary(false);
    setLoading(false);
    setTenorList([]);
    setTenors([]);
    setLandingPrefill(null);
    setLandingNotice("");
    setLandingRequiredFields([]);
    setActiveTab("summary");
    setActiveChartTab("prices");

    localStorage.removeItem("bestx_exec_ts");
    localStorage.removeItem("bestx_exec_rate");
    localStorage.removeItem("bestx_exec_fwd");
    localStorage.removeItem("bestx_expiry");
  };
  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column", // Stack the components vertically
      height: "100%",
      minHeight: 0,
      width: "100%",
      padding: "12px",
      boxSizing: "border-box",
      overflow: "hidden",
      backgroundColor: theme.globallink.colors.DarkGrey[500], // Background color for the combo tile
    },
    chartSection: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      flexShrink: 0,
    },
    chartRow: {
      display: "flex",
      gap: "12px",
      width: "100%",
      minHeight: 0,
    },
    chartCard: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      borderRadius: "12px",
      padding: "8px",
      border: "1px solid rgba(255,255,255,0.08)",
      background: "rgba(255,255,255,0.04)",
      overflow: "hidden",
    },
    chartCardBody: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      overflow: "auto",
      borderRadius: "8px",
    },
    chartResizeGrip: {
      width: "44px",
      height: "12px",
      margin: "8px auto 0",
      flexShrink: 0,
      position: "relative",
      cursor: "row-resize",
      touchAction: "none",
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        borderRadius: "999px",
        background: "rgba(224,224,224,0.5)",
      },
      "&:hover::after": {
        background: "rgba(224,224,224,0.9)",
      },
    },
    chartTitle: {
      color: "#E0E0E0",
      fontSize: "13px",
      fontWeight: 700,
      marginBottom: "8px",
      lineHeight: "18px",
    },
    insightsContainer: {
      flex: 1, // Take up remaining space
      minHeight: 0,
      display: "flex",
      overflow: "hidden",
    },
    wideInsightsContainer: {
      flex: 1,
      minHeight: 0,
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
      gap: "12px",
      marginTop: "10px",
      overflow: "hidden",
    },
    tabBar: {
      display: "flex",
      gap: "16px",
      marginTop: "12px",
      paddingBottom: "4px",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
      flexWrap: "wrap",
    },
    // Spacing below the chart grip matches PreTradeTile.
    analysisTabBar: {
      marginTop: "16px",
      marginBottom: "4px",
    },
    tabButton: {
      border: 0,
      borderBottom: "2px solid transparent",
      borderRadius: 0,
      padding: "3px 0 5px",
      marginBottom: "-3px",
      background: "transparent",
      color: "rgba(224,224,224,0.74)",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: "13px",
      lineHeight: "18px",
      transition: "all 0.15s ease",
      "&:hover": {
        background: "transparent",
        color: "#FFFFFF",
      },
    },
    tabButtonActive: {
      borderBottomColor: theme.globallink.colors.Blue[500],
      color: "#FFFFFF",
      fontWeight: 700,
    },
    title: {
      color: "#E0E0E0",
      fontSize: "16px",
      fontWeight: "bolder",
      display: "flex",
      justifyContent: "space-between",
      padding: 0,
      alignItems: "center",
      
      borderBottom: "1px solid rgba(255, 255, 255, 0.1)", // Align items vertically in the center
    },
    popoverContainer: {
      display: "flex",
      justifyContent: "end",
      width: "100%",
    },
    topActions: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    summaryPanel: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      minHeight: 0,
      overflow: "auto",
    },
    chatPanel: {
      flex: 1,
      display: "flex",
      minWidth: 0,
      minHeight: 0,
    },
    // The settings view reuses BestxSummaryTileComponent's card, header and
    // button metrics so "Edit Settings" swaps content without the panel
    // changing size or style.
    summaryConfigCard: {
      flex: 1,
      width: "100%",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      padding: "14px",
      boxSizing: "border-box",
      overflow: "hidden",
      borderRadius: "12px",
      border: "1px solid rgba(255,255,255,0.14)",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
      color: "#E0E0E0",
    },
    summaryConfigHeaderRow: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: "12px",
      flexWrap: "wrap",
    },
    summaryConfigHeader: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      flex: 1,
      minWidth: 0,
    },
    summaryConfigTitle: {
      color: theme.globallink.colors.White[500],
      fontSize: "14px",
      fontWeight: 700,
      lineHeight: "18px",
      margin: 0,
    },
    summaryConfigSubtitle: {
      color: "rgba(224,224,224,0.72)",
      fontSize: "12px",
      lineHeight: "20px",
      marginTop: "2px",
      marginBottom: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    summaryConfigBody: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      display: "flex",
      flexDirection: "column",
      gap: "18px",
      paddingTop: "14px",
      borderTop: "1px solid rgba(255,255,255,0.08)",
    },
    summaryConfigSection: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      margin: 0,
      padding: 0,
      border: 0,
      minWidth: 0,
    },
    summaryConfigLabel: {
      padding: 0,
      margin: 0,
      fontSize: "11px",
      fontWeight: 700,
      lineHeight: "16px",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "rgba(224,224,224,0.6)",
    },
    // Inputs stay native (keyboard, focus, screen readers) but are visually
    // hidden; the adjacent span draws the control.
    visuallyHiddenInput: {
      position: "absolute",
      width: "1px",
      height: "1px",
      margin: "-1px",
      padding: 0,
      border: 0,
      overflow: "hidden",
      clip: "rect(0 0 0 0)",
      whiteSpace: "nowrap",
    },
    segmentedControl: {
      display: "inline-flex",
      alignSelf: "flex-start",
      padding: "3px",
      gap: "2px",
      borderRadius: "9px",
      border: "1px solid rgba(255,255,255,0.1)",
      background: "rgba(0,0,0,0.18)",
    },
    segmentOption: {
      position: "relative",
      display: "flex",
      margin: 0,
      cursor: "pointer",
    },
    segmentOptionDisabled: {
      cursor: "not-allowed",
      opacity: 0.55,
    },
    segmentText: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "88px",
      height: "28px",
      padding: "0 14px",
      borderRadius: "6px",
      color: "rgba(224,224,224,0.7)",
      fontSize: "12px",
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      transition: "background 0.15s ease, color 0.15s ease",
      "$segmentOption:hover > input:not(:checked) + &": {
        color: "#FFFFFF",
        background: "rgba(255,255,255,0.05)",
      },
      "input:checked + &": {
        color: "#FFFFFF",
        background: theme.globallink.colors.Blue[500],
        boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
      },
      "input:focus-visible + &": {
        outline: `2px solid ${theme.globallink.colors.Blue[300]}`,
        outlineOffset: "2px",
      },
    },
    sourceGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
      gap: "8px",
    },
    sourceOption: {
      position: "relative",
      display: "flex",
      margin: 0,
      minWidth: 0,
      cursor: "pointer",
    },
    sourceOptionDisabled: {
      cursor: "not-allowed",
    },
    sourceCard: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: "10px",
      height: "40px",
      padding: "0 12px",
      boxSizing: "border-box",
      borderRadius: "8px",
      border: "1px solid rgba(255,255,255,0.1)",
      background: "rgba(255,255,255,0.03)",
      color: "rgba(224,224,224,0.78)",
      fontSize: "13px",
      fontWeight: 500,
      transition:
        "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
      "$sourceOption:hover > input:not(:disabled) + &": {
        borderColor: "rgba(255,255,255,0.2)",
        background: "rgba(255,255,255,0.06)",
      },
      "input:checked + &": {
        borderColor: "rgba(96, 165, 250, 0.5)",
        background: "rgba(37, 99, 235, 0.14)",
        color: "#FFFFFF",
      },
      "$sourceOption:hover > input:checked:not(:disabled) + &": {
        background: "rgba(37, 99, 235, 0.2)",
      },
      "input:focus-visible + &": {
        outline: `2px solid ${theme.globallink.colors.Blue[300]}`,
        outlineOffset: "2px",
      },
      "input:disabled:not(:checked) + &": {
        opacity: 0.55,
      },
    },
    sourceCheckmark: {
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "16px",
      height: "16px",
      boxSizing: "border-box",
      borderRadius: "4px",
      border: "1.5px solid rgba(224,224,224,0.45)",
      color: "transparent",
      transition: "all 0.15s ease",
      "input:checked + $sourceCard > &": {
        borderColor: theme.globallink.colors.Blue[500],
        background: theme.globallink.colors.Blue[500],
        color: "#FFFFFF",
      },
    },
    sourceOptionLabel: {
      flex: 1,
      minWidth: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    sourceOptionMeta: {
      flexShrink: 0,
      padding: "1px 6px",
      borderRadius: "4px",
      background: "rgba(255,255,255,0.08)",
      color: "rgba(224,224,224,0.7)",
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    },
    sourceHint: {
      fontSize: "12px",
      lineHeight: "18px",
      color: "rgba(224,224,224,0.6)",
      margin: 0,
    },
    summarySelectionCard: {
      borderRadius: "10px",
      padding: "14px",
      border: "1px solid rgba(255,255,255,0.08)",
      background:
        "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
      color: "#E0E0E0",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    summarySelectionTop: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "12px",
      flexWrap: "wrap",
    },
    summarySelectionText: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      minWidth: 0,
      flex: 1,
    },
    summarySelectionTitle: {
      fontSize: "14px",
      fontWeight: 700,
      color: "#E0E0E0",
      margin: 0,
    },
    summarySelectionSubtitle: {
      fontSize: "12px",
      lineHeight: "18px",
      color: "rgba(224,224,224,0.68)",
      margin: 0,
    },
    summarySelectionActions: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexWrap: "wrap",
    },
    summarySecondaryButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "8px",
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.05)",
      color: theme.globallink.colors.White[500],
      padding: "8px 12px",
      fontSize: "12px",
      fontWeight: 700,
      lineHeight: "16px",
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": {
        background: "rgba(255,255,255,0.1)",
      },
      "&:disabled": {
        opacity: 0.55,
        cursor: "not-allowed",
      },
    },
    summarySelectionPills: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
    },
    summarySelectionPill: {
      borderRadius: "999px",
      padding: "6px 12px",
      border: "1px solid rgba(255,255,255,0.12)",
      background: "rgba(255,255,255,0.05)",
      color: "#E0E0E0",
      fontSize: "12px",
      lineHeight: "16px",
      fontWeight: 600,
    },
    summarySelectionPillPrimary: {
      background: "rgba(37, 99, 235, 0.22)",
      borderColor: "rgba(96, 165, 250, 0.42)",
      color: "#DCEBFF",
    },
    generateSummaryButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "144px",
      borderRadius: "8px",
      border: `1px solid ${theme.globallink.colors.Blue[500]}`,
      background: theme.globallink.colors.Blue[500],
      color: "#FFFFFF",
      padding: "8px 12px",
      fontSize: "12px",
      fontWeight: 700,
      lineHeight: "16px",
      cursor: "pointer",
      transition: "opacity 0.15s ease",
      "&:hover": {
        opacity: 0.9,
      },
      "&:disabled": {
        opacity: 0.55,
        cursor: "not-allowed",
      },
    },
    summaryPlaceholder: {
      borderRadius: "10px",
      padding: "18px",
      border: "1px dashed rgba(255,255,255,0.12)",
      background: "rgba(255,255,255,0.02)",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },
    summaryPlaceholderTitle: {
      color: "#E0E0E0",
      fontSize: "14px",
      fontWeight: 700,
      margin: 0,
    },
    summaryPlaceholderText: {
      color: "rgba(224,224,224,0.72)",
      fontSize: "12px",
      lineHeight: "18px",
      margin: 0,
    },
    resetButton: {
      border: "1px solid transparent",
      borderRadius: "6px",
      padding: "4px 6px",
      background: "transparent",
      color: "rgba(224,224,224,0.74)",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: "12px",
      transition: "all 0.15s ease",
      "&:hover": {
        background: "rgba(255,255,255,0.06)",
        color: "#FFFFFF",
      },
    },
  });

  const classes = useStyles();
  const chartResizeStart = useRef<{
    pointerId: number;
    startY: number;
    startHeight: number;
  } | null>(null);
  const handleChartResize = (height: number) =>
    setChartHeight(savePreferredChartHeight(height));
  const getDraggedChartHeight = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const resizeStart = chartResizeStart.current;
    return resizeStart?.pointerId === event.pointerId
      ? clampChartHeight(
          resizeStart.startHeight + event.clientY - resizeStart.startY
        )
      : null;
  };
  const resizableChartProps = {
    style: {
      flex: "0 0 auto",
      height: `${chartHeight}px`,
      minHeight: "150px",
      maxHeight: "500px",
    },
  };
  const chartResizeGripProps = {
    title: "Drag to resize the chart",
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      chartResizeStart.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startHeight: chartHeight,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
      const height = getDraggedChartHeight(event);
      if (height !== null) setChartHeight(height);
    },
    onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
      const height = getDraggedChartHeight(event);
      chartResizeStart.current = null;
      if (height !== null) handleChartResize(height);
    },
    onPointerCancel: () => {
      chartResizeStart.current = null;
    },
  };

  useEffect(() => {
    const updateChartLayout = () => {
      const isCompact = isCompactChartLayout();
      setUseCompactCharts(isCompact);
      if (isCompact) {
        setActiveChartTab((tab) => (tab === "split" ? "prices" : tab));
      }
    };

    window.addEventListener("resize", updateChartLayout);
    return () => window.removeEventListener("resize", updateChartLayout);
  }, []);

  useEffect(() => {
    if (!useContext) return;

    try {
      if (!context) {
        console.log("No Context Available");
        return;
      }

      const nextLandingPrefill = parseBestXTradeContext(context);

      if (nextLandingPrefill) {
        const missingFields = getBestXMissingFields(nextLandingPrefill);

        if (summaryRequestCleanupRef.current) {
          summaryRequestCleanupRef.current();
          summaryRequestCleanupRef.current = null;
        }
        setLandingPrefill(nextLandingPrefill);
        setCurrencyPair(nextLandingPrefill.currencyPair);
        setExecutionTimestamp(nextLandingPrefill.executionTimestamp);
        setExecutionSpotRate(nextLandingPrefill.executionSpotRate);
        setExecutionForwardPoints(nextLandingPrefill.executionForwardPoints);
        setExpiration_timestamp(
          toDateInputValue(nextLandingPrefill.expirationTimestamp)
        );
        setSummary("");
        summaryCacheRef.current = "";
        summaryVersionRef.current = 0;
        setAnalysisWindow(DEFAULT_ANALYSIS_WINDOW);
        setAnalysisSources(DEFAULT_ANALYSIS_SOURCES);
        setIsSummaryConfigOpen(false);
        setActiveTab("summary");

        if (missingFields.length > 0) {
          setLandingRequiredFields(missingFields);
          setLandingNotice(
            "We received partial trade details. Please complete the missing fields below to continue."
          );
          setAutoGenerateSummary(false);
          setHasSubmitted(false);
          return;
        }

        setLandingRequiredFields([]);
        setLandingNotice("");
        setAutoGenerateSummary(true);
        setHasSubmitted(true);
        return;
      }

      console.warn("Ignoring incomplete or unsupported trade context.", context);
    } catch {
      console.log("Some Error Happened");
    }
  }, [context, useContext]);

  useEffect(() => {
    setSessionVariable(currencyPair);
  }, [currencyPair]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia(WIDE_ANALYSIS_LAYOUT_QUERY);
    const updateLayout = () => setIsWideLayout(mediaQuery.matches);

    updateLayout();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateLayout);
      return () => mediaQuery.removeEventListener("change", updateLayout);
    }

    mediaQuery.addListener(updateLayout);
    return () => mediaQuery.removeListener(updateLayout);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 580px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setUseCompactCharts(event.matches);
    };

    setUseCompactCharts(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    return () => {
      if (summaryRequestCleanupRef.current) {
        summaryRequestCleanupRef.current();
        summaryRequestCleanupRef.current = null;
      }
    };
  }, []);

  const handleSourceToggle = (sourceKey: keyof BestXAnalysisSources) => {
    if (sourceKey === "prices") return;
    setAnalysisSources((prev) => ({
      ...prev,
      [sourceKey]: !prev[sourceKey],
      prices: true,
    }));
  };

  useEffect(() => {
    if (
      !autoGenerateSummary ||
      !hasSubmitted ||
      !currencyPair ||
      !executionTimestamp
    ) {
      return;
    }

    setAutoGenerateSummary(false);
    if (summaryRequestCleanupRef.current) {
      summaryRequestCleanupRef.current();
      summaryRequestCleanupRef.current = null;
    }
    summaryCacheRef.current = "";
    summaryVersionRef.current = 0;
    setIsSummaryConfigOpen(false);

    summaryRequestCleanupRef.current = handleFetchSummary({
      currencyPair,
      executionTimestamp,
      executionSpotRate,
      expiration_timestamp,
      executionForwardPoints,
      analysisWindow,
      analysisSources,
      setSummary,
      summaryCacheRef,
      summaryVersionRef,
      setLoading,
    });
  }, [
    autoGenerateSummary,
    hasSubmitted,
    currencyPair,
    executionTimestamp,
    executionSpotRate,
    expiration_timestamp,
    executionForwardPoints,
    analysisWindow,
    analysisSources,
  ]);

  const handleGenerateSummary = () => {
    if (!currencyPair || !executionTimestamp || loading) return;

    if (summaryRequestCleanupRef.current) {
      summaryRequestCleanupRef.current();
      summaryRequestCleanupRef.current = null;
    }

    summaryCacheRef.current = "";
    summaryVersionRef.current = 0;
    setIsSummaryConfigOpen(false);

    summaryRequestCleanupRef.current = handleFetchSummary({
      currencyPair,
      executionTimestamp,
      executionSpotRate,
      expiration_timestamp,
      executionForwardPoints,
      analysisWindow,
      analysisSources,
      setSummary,
      summaryCacheRef,
      summaryVersionRef,
      setLoading,
    });
  };

  const selectedSummarySources = (
    Object.keys(analysisSources) as Array<keyof BestXAnalysisSources>
  )
    .filter((sourceKey) => analysisSources[sourceKey])
    .map((sourceKey) => ANALYSIS_SOURCE_LABELS[sourceKey]);

  // Snapshot settings when opening the editor so "Back" can discard edits and
  // keep the pills in sync with the summary that's actually shown.
  const summaryConfigSnapshotRef = useRef<{
    analysisWindow: BestXAnalysisWindow;
    analysisSources: BestXAnalysisSources;
  } | null>(null);

  const handleOpenSummaryConfig = () => {
    summaryConfigSnapshotRef.current = { analysisWindow, analysisSources };
    setIsSummaryConfigOpen(true);
  };

  const handleCloseSummaryConfig = () => {
    const snapshot = summaryConfigSnapshotRef.current;
    if (snapshot) {
      setAnalysisWindow(snapshot.analysisWindow);
      setAnalysisSources(snapshot.analysisSources);
    }
    summaryConfigSnapshotRef.current = null;
    setIsSummaryConfigOpen(false);
  };

  const shouldShowSummaryConfig = isSummaryConfigOpen;
  const summaryActionCopy = summary
    ? "Change the time window or sources, then regenerate the summary."
    : "The summary will cover the selected time window and market conditions around the completion time.";


  if (!hasSubmitted) {
    return (
      <BestXLandingPage
        currencyList={currencyList}
        onSubmit={handleTradeSubmit}
        initialTrade={landingPrefill ?? undefined}
        notice={landingNotice}
        requiredFields={landingRequiredFields}
      />
    );
  }

  return (
    <div className={classes.container} key={submissionVersion}>

      <div className={classes.title}>

        <BestXTradeHeader
          currencyPair={currencyPair}
          executionTimestampUtc= {executionTimestamp}
          executionSpotRate={executionSpotRate}
          expiryDateUtc={expiration_timestamp}
          executionForwardPoints={executionForwardPoints}
        />
        
        <div className={classes.topActions}>
          <button
            type="button"
            className={classes.resetButton}
            onClick={handleResetToLanding}
          >
            Reset
          </button>
          <BestXPopOverMenu
            useContext={useContext}
            currencyPair={currencyPair}
            currencyList={currencyList}
            tenorList={tenorList}
            setTenor={setTenors}
            setUserLanguage={setUserLanguage}
            userLanguage={userLanguage}
            executionTimestamp={executionTimestamp}
            executionSpotRate={executionSpotRate}
            executionForwardPoints={executionForwardPoints}
            expirationTimestamp={expiration_timestamp}
            onSubmit={handleTradeSubmit}
          />
        </div>
      </div>
      <div className={classes.chartSection}>
        <div
          className={classes.tabBar}
          role="group"
          aria-label="Market data view"
        >
          {!useCompactCharts && (
            <button
              type="button"
              className={`${classes.tabButton} ${
                activeChartTab === "split" ? classes.tabButtonActive : ""
              }`}
              onClick={() => setActiveChartTab("split")}
              aria-pressed={activeChartTab === "split"}
            >
              Split
            </button>
          )}
          <button
            type="button"
            className={`${classes.tabButton} ${
              activeChartTab === "prices" ? classes.tabButtonActive : ""
            }`}
            onClick={() => setActiveChartTab("prices")}
            aria-pressed={activeChartTab === "prices"}
          >
            Spot
          </button>
          <button
            type="button"
            className={`${classes.tabButton} ${
              activeChartTab === "forward" ? classes.tabButtonActive : ""
            }`}
            onClick={() => setActiveChartTab("forward")}
            aria-pressed={activeChartTab === "forward"}
          >
            Forward
          </button>
        </div>
        {activeChartTab === "split" ? (
          <div className={classes.chartRow}>
            <div className={classes.chartCard}>
              <div className={classes.chartTitle}>Spot Prices</div>
              <div
                className={classes.chartCardBody}
                {...resizableChartProps}
              >
                <BestXPriceChartTileComponent
                  currencyPair={currencyPair}
                  executionTimestamp={executionTimestamp}
                  priceWindowMs={BESTX_PRICE_WINDOW_MS}
                  chartHeight={chartHeight}
                  preferStoredQuotes
                />
              </div>
            </div>
            <div className={classes.chartCard}>
              <div className={classes.chartTitle}>Forward Points</div>
              <div
                className={classes.chartCardBody}
                {...resizableChartProps}
              >
                <CoTraderForwardChart
                  currencyPair={currencyPair}
                  setTenorList={handleSetTenorList}
                  tenors={tenors}
                  executionTimestamp={executionTimestamp}
                  chartHeight={chartHeight}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={classes.chartCard}>
            <div
              className={classes.chartCardBody}
              {...resizableChartProps}
            >
              {activeChartTab === "prices" ? (
                <BestXPriceChartTileComponent
                  currencyPair={currencyPair}
                  executionTimestamp={executionTimestamp}
                  priceWindowMs={BESTX_PRICE_WINDOW_MS}
                  chartHeight={chartHeight}
                  preferStoredQuotes
                />
              ) : (
                <CoTraderForwardChart
                  currencyPair={currencyPair}
                  setTenorList={handleSetTenorList}
                  tenors={tenors}
                  executionTimestamp={executionTimestamp}
                  chartHeight={chartHeight}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <div className={classes.chartResizeGrip} {...chartResizeGripProps} />
      {/* Wide screens show Summary and chat side by side; narrower ones
          switch between them with tabs. */}
      {isWideLayout ? null : (
        <div
          className={`${classes.tabBar} ${classes.analysisTabBar}`}
          role="group"
          aria-label="Trade analysis view"
        >
          <button
            type="button"
            className={`${classes.tabButton} ${
              activeTab === "summary" ? classes.tabButtonActive : ""
            }`}
            onClick={() => setActiveTab("summary")}
            aria-pressed={activeTab === "summary"}
          >
            Summary
          </button>
          <button
            type="button"
            className={`${classes.tabButton} ${
              activeTab === "chat" ? classes.tabButtonActive : ""
            }`}
            onClick={() => setActiveTab("chat")}
            aria-pressed={activeTab === "chat"}
          >
            Ask EMAlpha
          </button>
        </div>
      )}
      <div
        className={
          isWideLayout
            ? classes.wideInsightsContainer
            : classes.insightsContainer
        }
      >
        {/* Both panels stay mounted in the same position across tabs and
            layouts so switching never discards the chat conversation (or its
            socket) or an in-flight summary; inactive tabs are only hidden. */}
        <div
          className={classes.summaryPanel}
          style={
            isWideLayout || activeTab === "summary"
              ? undefined
              : { display: "none" }
          }
        >
          {shouldShowSummaryConfig ? (
            <div className={classes.summaryConfigCard}>
              <div className={classes.summaryConfigHeaderRow}>
                <div className={classes.summaryConfigHeader}>
                  <p className={classes.summaryConfigTitle}>Market Summary</p>
                  <p className={classes.summaryConfigSubtitle}>
                    Summary settings
                  </p>
                </div>
                <div className={classes.summarySelectionActions}>
                  <button
                    type="button"
                    className={classes.summarySecondaryButton}
                    onClick={handleCloseSummaryConfig}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className={classes.generateSummaryButton}
                    onClick={handleGenerateSummary}
                    disabled={loading || !currencyPair || !executionTimestamp}
                  >
                    {loading
                      ? "Generating…"
                      : summary
                        ? "Regenerate Summary"
                        : "Generate Summary"}
                  </button>
                </div>
              </div>

              <div className={classes.summaryConfigBody}>
                <fieldset className={classes.summaryConfigSection}>
                  <legend className={classes.summaryConfigLabel}>
                    Time window around completion
                  </legend>
                  <div className={classes.segmentedControl}>
                    {(
                      Object.keys(ANALYSIS_WINDOW_LABELS) as BestXAnalysisWindow[]
                    ).map((windowKey) => (
                      <label
                        key={windowKey}
                        className={`${classes.segmentOption} ${
                          loading ? classes.segmentOptionDisabled : ""
                        }`}
                      >
                        <input
                          className={classes.visuallyHiddenInput}
                          type="radio"
                          name="bestx-summary-window"
                          value={windowKey}
                          checked={analysisWindow === windowKey}
                          onChange={() => setAnalysisWindow(windowKey)}
                          disabled={loading}
                        />
                        <span className={classes.segmentText}>
                          {ANALYSIS_WINDOW_LABELS[windowKey]}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset className={classes.summaryConfigSection}>
                  <legend className={classes.summaryConfigLabel}>Sources</legend>
                  <div className={classes.sourceGrid}>
                    {SUMMARY_SOURCE_OPTIONS.map(({ key, label }) => {
                      const isRequired = key === "prices";
                      const isDisabled = isRequired || loading;

                      return (
                        <label
                          key={key}
                          className={`${classes.sourceOption} ${
                            isDisabled ? classes.sourceOptionDisabled : ""
                          }`}
                        >
                          <input
                            className={classes.visuallyHiddenInput}
                            type="checkbox"
                            checked={analysisSources[key]}
                            onChange={
                              isRequired
                                ? undefined
                                : () => handleSourceToggle(key)
                            }
                            disabled={isDisabled}
                          />
                          <span className={classes.sourceCard}>
                            <span
                              className={classes.sourceCheckmark}
                              aria-hidden="true"
                            >
                              <svg width="10" height="10" viewBox="0 0 12 12">
                                <path
                                  d="M2.5 6.2 4.9 8.6 9.5 3.6"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <span className={classes.sourceOptionLabel}>
                              {label}
                            </span>
                            {isRequired ? (
                              <span className={classes.sourceOptionMeta}>
                                Required
                              </span>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <p className={classes.sourceHint}>
                    {summaryActionCopy} Price data is always included; other
                    sources only change the evidence used.
                  </p>
                </fieldset>
              </div>
            </div>
          ) : null}

          {!shouldShowSummaryConfig && (
            <BestxSummaryTileComponent
              summary={summary}
              loading={loading}
              title="Market Summary"
              settingsPills={[
                `${ANALYSIS_WINDOW_LABELS[analysisWindow]} window`,
                ...selectedSummarySources,
              ]}
              onEditSettings={handleOpenSummaryConfig}
            />
          )}
        </div>
        <div
          className={classes.chatPanel}
          style={
            isWideLayout || activeTab === "chat" ? undefined : { display: "none" }
          }
        >
          <BestXConversationTileComponent
            currencyPair={currencyPair}
            executionTimestamp={executionTimestamp}
            executionSpotRate={executionSpotRate}
            executionForwardPoints={executionForwardPoints}
            expirationTimestamp={expiration_timestamp}
            analysisWindow={analysisWindow}
            analysisSources={analysisSources}
            userLanguage={userLanguage}
          />
        </div>
      </div>
    </div>
  );
};

export default BestXTile1;



const handleFetchSummary = ({
  setSummary,
  summaryCacheRef,
  summaryVersionRef,
  currencyPair,
  executionTimestamp,
  executionSpotRate,
  expiration_timestamp,
  setLoading,
  executionForwardPoints,
  analysisWindow,
  analysisSources,
}: any) => {
  setLoading(true);
  setSummary("");

  const user_id:any = localStorage.getItem("userId");
  const userTimeZone = BESTX_REQUEST_TIME_ZONE;
  const userLanguage = localStorage.getItem("user_language") || "English";
  const random_number = Math.floor(100000 + Math.random() * 900000);
  const chat_id = `BESTXGPT-${user_id}-${random_number}`;

  const requestBody = {
    chat_id,
    origin: "BESTX",
    currency_pair: currencyPair ?? null,
    execution_timestamp: executionTimestamp ?? null,
    execution_timezone: userTimeZone,
    execution_spot_rate: executionSpotRate ?? null,
    execution_forward_points: executionForwardPoints ?? null,
    expiration_timestamp,
    embedding_model: "text-embedding-ada-002",
    summary_profile: "market_window_context_v1",
    analysis_window: analysisWindow,
    analysis_sources: analysisSources,
    userTimeZone,
    user_language: userLanguage,
  };

  let isCancelled = false;
  let ws: WebSocket | null = null;

  const openSocket = async () => {
    try {
      const wsUrl = await getAuthenticatedWebSocketUrl(chat_id, user_id);
      if (isCancelled) return;

      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        ws?.send(JSON.stringify(requestBody));
      };

      ws.onmessage = (event) => {
        let data: any;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }

        const t = data?.type;
        const msg = data?.message;

        if (t === "FINISH" || t === "DONE" || t === "END" || t === "COMPLETE") {
          setLoading(false);
          try {
            ws?.close();
          } catch {}
          return;
        }
        if (t === "SUMMARY_UPDATE") {
          const summaryPayload = parseSummaryPayload(msg);
          summaryCacheRef.current = summaryPayload?.summary || "";
          summaryVersionRef.current = summaryPayload?.version || 0;
          return;
        }
        if (t === "INFO" || t === "MESSAGE_ID") {
          return;
        }
        if (t === "ERROR") {
          setSummary((prev: string) => `${prev}\n\nError: ${String(msg || "")}`);
          return;
        }
        if (t === "urls") {
          setSummary((prev: string) => `${prev}${formatSummaryUrlList(String(msg || ""))}`);
          return;
        }

        if (msg != null) {
          setSummary((prev: string) => prev + String(msg));
        }
      };

      ws.onerror = (e) => {
        console.error("WS error", e);
        setLoading(false);
      };
    } catch (error) {
      if (!isCancelled) {
        console.error("WS auth failed", error);
        setLoading(false);
      }
    }
  };

  void openSocket();

  // cleanup only closes if still active
  return () => {
    isCancelled = true;
    try { ws?.close(); } catch {}
  };
};
